import { useMemo, useRef, useState } from 'react';
import {
  INTAKE_DOC_TYPES, OCCUPANCIES, LOAN_PURPOSES, CITIZENSHIPS, PROPERTY_TYPES,
  CREDIT_EVENTS, US_STATES, intakeSchema, computeLtv, loanAmountAtLtv,
  missingLtvInputs, formShapeOf, formFieldLabels, FREE_TEXT_QUESTION,
  type Intake,
} from '@lenderhound/shared';

/**
 * The intake form.
 *
 * EVERY QUESTION IS REQUIRED EXCEPT RESERVES. Misha, 2026-09-08: "make all fields except
 * the 13.Reserves field MANDATORY. so 12 mandatory fields." That is twelve on the form a
 * broker opens - transaction, property value, LTV, loan amount, occupancy, property type,
 * state, FICO, documentation, the qualifying ratio, borrower status and credit event -
 * and the conditional questions are required too whenever they are on screen, because a
 * question that only appears when it applies is a question that applies.
 *
 * This REVERSES Henry, 2026-08-27: "on our GUI, none of the pulldown fields need to be
 * required fields, they should all be optional." That instruction is not being forgotten,
 * it is being overruled, and the reason it was reasonable then is the reason it stopped
 * being: a blank widens the grid sweep rather than matching nothing, so the search still
 * returns - but it returns an answer priced off the most permissive row on that axis,
 * which is a number about a file nobody has. Henry's own complaints since have been about
 * exactly that shape of answer ("Broker left reserve field blank so for some reason that
 * made it unknown"). The widening did not go away; it is simply no longer reachable
 * through the form for the eleven axes that decide which grid a file is even read on.
 *
 * Reserves keeps the exemption because reserves is the one Henry named as wrong to demand:
 * a lender that prints no reserve rule has nothing to test a figure against, so a blank
 * there costs an answer nothing. It is marked optional on screen, and it is the ONLY
 * marker on the form - twelve "required" chips beside twelve required fields is noise,
 * and the single exception is the thing worth pointing at.
 *
 * State defaults to CA rather than to blank, on the same instruction. It is a default and
 * not a lock: it can be changed like any other answer, and it is submitted like any other
 * answer.
 *
 * Every dropdown is generated from the shared vocabulary rather than typed out here.
 * The prototype carried two conflicting program lists that had already drifted apart,
 * and a form option the database has never heard of produces a silent zero-result
 * search that looks like "no lender can do this" - the one answer this product must
 * never give by accident.
 */

const FUNKY_MAX = 2000;

interface Props {
  onSubmit: (intake: Intake) => void;
  busy: boolean;
  /**
   * The broker's first edit of any box, once per mount. For the traffic funnel: Misha,
   * 2026-10-20, asked to tell "landed and left" from "started and gave up".
   */
  onStarted?: () => void;
  /** Search pressed and refused by the form, with the keys of the boxes it flagged. */
  onBlocked?: (fields: string[]) => void;
}

type Draft = Record<string, string>;

const initialDraft: Draft = {
  loanPurpose: '', propertyValue: '', loanAmount: '', ltv: '', cashInHand: '',
  occupancy: '', propertyType: '', unitCount: '',
  // CA, not blank. Misha, 2026-09-08: "for state, default to 'CA'". It is the state most
  // of this book is written in, and now that the field is required a sensible default is
  // one fewer dropdown between a broker and an answer. Still an ordinary answer: it
  // submits as 'CA' and it can be changed to any other state.
  state: 'CA', ficoScore: '', docType: '', dti: '', dscr: '', citizenship: '',
  creditEvent: 'none', creditEventSeasoningMonths: '', reservesMonths: '', funkyText: '',
};

const digits = (s: string): string => s.replace(/[^0-9]/g, '');
const money = (s: string): string => {
  const n = digits(s);
  return n ? Number(n).toLocaleString() : '';
};

/**
 * Both of these live at module scope, and that is not a style preference.
 *
 * They were defined inside ScenarioDesk, closing over `errors`, `d` and `set` - which
 * reads well and made every text field in the form unusable. A component declared in a
 * render body is a NEW function on every render, so React sees a different element type
 * in the same position, unmounts the subtree and mounts a fresh one. The <input> the
 * user was typing into is destroyed and replaced between keystrokes: the DOM node that
 * held focus no longer exists, so the caret lands nowhere and the next character goes
 * to the page. One letter per click.
 *
 * The closure is what the props below replace. Nothing may capture render-scoped state
 * here; if these need something, it is passed.
 */
function Field({ name, label, note, error, children }: {
  name: string; label: string; note?: { text: string; tone: 'opt' | 'req' } | undefined;
  error?: string | undefined; children: React.ReactNode;
}) {
  return (
    <div>
      {/*
        The "optional" / "required" marker sits beside the <label> rather than inside it.
        The label element's text IS the field's accessible name, and the name has to stay
        the name - "10. DTI %", not "10. DTI % optional" - or every lookup by label, from a
        screen reader to the test suite, is matching a string that includes a note about
        the field rather than the field.
      */}
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        gap: 8, marginBottom: 6,
      }}>
        <label className="lbl" htmlFor={name} style={{ marginBottom: 0 }}>{label}</label>
        {note ? <span className={`lbl ${note.tone}`} style={{ marginBottom: 0 }}>{note.text}</span> : null}
      </div>
      {children}
      {error ? <div className="err-text">{error}</div> : null}
    </div>
  );
}

/**
 * `placeholder` is "Select…" on every one of these, and it went back to being an
 * instruction when the fields went back to being required.
 *
 * It read "Any" for eleven days, and that was correct for eleven days: a blank widened the
 * sweep, so "Any" said what leaving it alone would do and "Select…" would have nagged
 * about a decision the broker had already made. None of that is true now. An unanswered
 * dropdown is refused, so the blank state has to read as unfinished - which is exactly
 * what "Select…" reads as, and why it was wrong before.
 */
function Select({ name, options, placeholder, value, error, onChange }: {
  name: string;
  options: readonly { code: string; label: string }[];
  placeholder: string;
  value: string;
  error?: string | undefined;
  onChange: (v: string) => void;
}) {
  return (
    <select
      id={name}
      className={`fselect${error ? ' field-error' : ''}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {/*
        No empty option when there is nothing to choose between. A one-option select still
        rendering "Select…" offers the broker a way to UN-answer a question that has a
        single answer, and every field but reserves is required - so the only thing behind
        that option is a red message under a box with one legal value in it. Reached today
        by the occupancy list under a cash-flow doc type.
      */}
      {options.length > 1 ? <option value="">{placeholder}</option> : null}
      {options.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
    </select>
  );
}

/**
 * What a person should read under a field they have not filled in.
 *
 * There is an entry for every required field, and every field is required but reserves.
 * The table shrank to three when blanks became legal and it has grown back, and the
 * sentences are not the ones it used to hold: each says what the axis DECIDES, not that
 * the box is empty. A broker can see that the box is empty. What they cannot see is that
 * leaving Documentation alone does not broaden the search so much as decline to say which
 * product is being priced, or that State is a licensing question before it is a pricing
 * one.
 *
 * Zod's own message is written for the developer who wrote the schema, and is what these
 * replace. An untouched State dropdown produced "Invalid enum value. Expected 'AL' |
 * 'AK' | 'AZ' | ..." - four hundred characters listing every state in the union,
 * rendered as a wall of text under a select. These are reached before the schema is,
 * because a blank is now caught here; the same treatment still applies to a wrong value,
 * which is VALUE_ERRORS below.
 */
const FIELD_ERRORS: Record<string, string> = {
  loanPurpose:
    'Choose the transaction. Purchase, rate/term and cash-out are separate grids at every '
    + 'lender, not degrees of the same one, so this picks which grid the file is read on.',
  propertyValue:
    'Enter the property value. Every lender grid is a maximum LTV, so this and the loan '
    + 'amount are the two figures the search cannot run without.',
  loanAmount:
    'Enter the loan amount. Every lender grid is a maximum LTV, so this and the property '
    + 'value are the two figures the search cannot run without.',
  cashInHand:
    'Say what the borrower walks away with. Two lenders cap it above 65-70% LTV, so on a '
    + 'cash-out it is a grid dimension rather than a detail. Zero is a real answer - it '
    + 'says no cap can bind this file.',
  occupancy:
    'Choose the occupancy. Primary, second home and investment are priced off different '
    + 'grids, and several programs exist for only one of the three.',
  propertyType:
    'Choose the property type. Condos, 2-4 units and 5+ carry their own LTV ceilings, and '
    + 'some programs will not look at them at all.',
  unitCount:
    'Say how many units. Lenders stop at 8, 9 or 10, so the count is what decides which '
    + 'of them can look at the building.',
  state:
    'Choose the state. Lending licences and state-level overlays are per state, so this '
    + 'decides who may lend on the file before it decides anything about the rate.',
  ficoScore:
    'Enter the FICO. It is the axis every rate matrix is banded on. Enter 0 - and only 0 - '
    + 'for a borrower with no score at all; several lenders grid that.',
  docType:
    'Choose the documentation type. It selects the program, and it decides whether the '
    + 'next question is a DSCR or a DTI.',
  dti:
    'Enter the DTI. Every income-qualified program publishes a maximum, and a blank one '
    + 'cannot be held up against any of them.',
  dscr:
    'Enter the DSCR. It is what a cash-flow program qualifies the property on, and every '
    + 'one of them publishes a floor.',
  citizenship:
    'Choose the borrower status. Foreign national and ITIN files are priced off their own '
    + 'grids, not off a discount to the citizen one.',
  creditEventSeasoningMonths:
    'Say how many months ago the credit event was. Lenders price off that number, so '
    + 'leaving it out is a decline rather than a blank.',
};

/**
 * The questions that must be answered, given what the form is currently showing.
 *
 * Derived from the shape rather than listed twice, so a conditional field cannot be
 * required while hidden - which would be an unclearable error on a box that is not on
 * screen, the worst failure this change can produce.
 *
 * `ltv` is deliberately absent, and it is not an oversight. It is not a schema field and
 * never leaves this component; it is the property value and the loan amount expressed in
 * the units the broker happens to have, and either box fills the other. Requiring both
 * amounts IS requiring it, and a third message saying so under a box that auto-fills
 * would be the form complaining about a figure it is about to write itself.
 *
 * `reservesMonths` is absent because it is the exemption; see the note at the top.
 */
function requiredKeys(shape: {
  cashOut: boolean; unitsAsked: boolean; cashFlow: boolean; seasoningRelevant: boolean;
}): readonly string[] {
  return [
    'loanPurpose', 'propertyValue', 'loanAmount',
    ...(shape.cashOut ? ['cashInHand'] : []),
    'occupancy', 'propertyType',
    ...(shape.unitsAsked ? ['unitCount'] : []),
    'state', 'ficoScore', 'docType',
    shape.cashFlow ? 'dscr' : 'dti',
    'citizenship', 'creditEvent',
    ...(shape.seasoningRelevant ? ['creditEventSeasoningMonths'] : []),
  ];
}

/**
 * What a person should read under a field they DID fill in, wrongly.
 *
 * Every error in this form used to come out of FIELD_ERRORS, keyed on the field alone,
 * on the assumption that the only thing a broker can get wrong is not answering yet.
 * That is untrue for exactly the fields where being wrong is plausible, and the results
 * were bad in both directions.
 *
 * Type $1,000,000 of value against $1,500,000 of loan - a stray zero, the single most
 * common typo on this form, and one the LTV box beside it still flags in red - and the
 * schema's cross-field refine fires with a perfectly clear message, "Loan amount cannot
 * exceed property value". It was thrown away and replaced with "Enter the loan amount.",
 * printed under a field with $1,500,000 visibly sitting in it. The broker is told to do
 * the thing they have just done. (That one is not fixed by this table but by the
 * `custom` branch in submit(): a refine wrote its own sentence, and it is the sentence.)
 *
 * Type a DSCR of 115, meaning 1.15 - the other predictable mistake, since every other
 * ratio on a term sheet is quoted as a percentage - and it said "Enter the DSCR, or
 * leave it blank if it does not apply." That reads as "this field does not matter",
 * which is the reverse of the truth on a cash-flow file.
 *
 * So: FIELD_ERRORS when the box is empty, this when it is not. Anything without an entry
 * falls through to Zod's own message, which is at least true even when it is written in
 * the wrong language for the reader.
 */
const VALUE_ERRORS: Record<string, string> = {
  propertyValue: 'A property value has to be above zero and under $100,000,000.',
  loanAmount: 'A loan amount has to be above zero and under $100,000,000.',
  ficoScore:
    'A FICO is 300 to 850. Enter 0 — and only 0 — for a borrower with no score at all.',
  dti: 'DTI is a percentage of income: 0 to 100.',
  dscr:
    'DSCR is a ratio, not a percentage. A file that covers its payment 1.15 times is '
    + '1.15, not 115.',
  reservesMonths: 'Reserves are counted in months of PITIA: 0 to 120.',
  cashInHand:
    'Cash in hand is the money the borrower takes away at closing, in dollars, under '
    + '$100,000,000. Zero is a real answer - it says no cap can bind this file.',
  unitCount:
    'Five units or more. A smaller building is not a smaller number here, it is a '
    + 'different property type with its own option a couple of lines up the dropdown.',
  creditEventSeasoningMonths: 'Months since the credit event: 0 to 600.',
};

/**
 * The one thing the LTV box can be asked that it cannot answer.
 *
 * It is not in either table above because LTV is not a schema field - it never leaves
 * this component, and no Zod issue will ever be keyed on it (see the note on loanAmount
 * in intake.ts). The message still has to exist, because typing a percentage into an
 * empty form is the obvious first move for a broker who knows the LTV and not the loan
 * size, and a box that silently does nothing reads as a broken box rather than as a
 * missing prerequisite.
 */
const LTV_NEEDS_PROPERTY_VALUE =
  'Enter the property value first. LTV is a percentage of it, so there is nothing to '
  + 'take a percentage of yet - and it is the loan amount this produces that the search '
  + 'actually runs on.';

export default function ScenarioDesk({ onSubmit, busy, onStarted, onBlocked }: Props) {
  const [d, setD] = useState<Draft>(initialDraft);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const started = useRef(false);
  const touch = (): void => {
    if (started.current) return;
    started.current = true;
    onStarted?.();
  };
  /** Every refusal of a Search press goes through here, so each one is reported once. */
  const refuse = (errs: Record<string, string>): void => {
    setErrors(errs);
    onBlocked?.(Object.keys(errs));
  };

  const set = (k: string) => (v: string) => {
    touch();
    setD((prev) => ({ ...prev, [k]: v }));
    setErrors((prev) => (prev[k] ? { ...prev, [k]: '' } : prev));
  };

  const pv = Number(digits(d['propertyValue'] ?? ''));
  const typedLa = Number(digits(d['loanAmount'] ?? ''));
  const typedLtv = Number((d['ltv'] ?? '').replace(/[^0-9.]/g, ''));

  /*
   * The loan amount the form is SHOWING, which is not always the one in the loan amount
   * box.
   *
   * The two boxes are synced on blur (see below), and a broker who types an LTV and then
   * clicks Search never blurs in a way this render can see: the blur handler and the
   * click land in the same batch, so submit() reads a draft where loanAmount is still
   * empty and refuses a form with a perfectly good answer on screen. Deriving it here
   * rather than only in the blur makes the fallback structural instead of dependent on
   * event ordering - the box the broker filled in is the box that counts.
   */
  const la = typedLa > 0 ? typedLa
    : pv > 0 && typedLtv > 0 ? loanAmountAtLtv(pv, typedLtv)
      : 0;
  const ltv = pv > 0 && la > 0 ? computeLtv(pv, la) : null;
  const ltvColor = ltv === null ? 'var(--muted)'
    : ltv > 80 ? 'var(--red)' : ltv > 70 ? 'var(--amber)' : 'var(--green)';

  /**
   * Loan amount and LTV write into each other, ON BLUR.
   *
   * Henry, 2026-09-01: "more often than not the broker knows the ltv faster than the loan
   * amount", so either box may be the one that gets typed into and the other has to
   * follow. Only the loan amount is submitted - LTV is not a field on the schema, and
   * adding one would give the refine, the scenario and the near-miss logic a second
   * opinion of the same file (see the note on loanAmount in intake.ts). This is a
   * calculator that writes into the stored figure, not a second stored figure.
   *
   * On blur, and specifically not on change: bound per keystroke, the two boxes fight
   * each other while a person is typing. Enter "8" of an intended 80% LTV and the loan
   * amount box fills with 8% of the value; enter "1" of $1,500,000 and the LTV box reads
   * 0.00% - each half-typed number is rewritten as a finished one, and on a form where
   * the red LTV warning is the stray-zero check, that is noise exactly where the signal
   * is supposed to be.
   *
   * The pair is normalised together rather than each side rounded on its own: after the
   * LTV box produces an amount, the box is rewritten with that amount's LTV. 79.999% of
   * $2,000,000 is $1,599,980, which is 80.00%, and the form must not show a number the
   * figure beside it contradicts.
   */
  const sync = (source: 'propertyValue' | 'loanAmount' | 'ltv') => () => {
    // Said in words rather than swallowed: an LTV with no property value is a percentage
    // of nothing, and a box that quietly does nothing reads as broken rather than as a
    // prerequisite that has not been met yet.
    if (source === 'ltv' && typedLtv > 0 && !(pv > 0)) {
      setErrors((prev) => ({ ...prev, ltv: LTV_NEEDS_PROPERTY_VALUE }));
      return;
    }
    setD((prev) => {
      const value = Number(digits(prev['propertyValue'] ?? ''));
      if (!(value > 0)) return prev;
      const amount = Number(digits(prev['loanAmount'] ?? ''));
      const pct = Number((prev['ltv'] ?? '').replace(/[^0-9.]/g, ''));
      const fromPct = (): Draft => {
        const derived = loanAmountAtLtv(value, pct);
        return {
          ...prev,
          loanAmount: money(String(derived)),
          ltv: computeLtv(value, derived).toFixed(2),
        };
      };
      if (source === 'ltv') return pct > 0 ? fromPct() : prev;
      // An emptied loan amount empties the percentage with it. The alternative is that
      // clearing the box appears to work and then the stale LTV silently puts the old
      // amount back at submit, which is the form arguing with the broker about a figure
      // they have just deleted.
      if (source === 'loanAmount' && !(amount > 0)) {
        return pct > 0 ? { ...prev, ltv: '' } : prev;
      }
      // Otherwise the loan amount is the answer and the percentage follows it - including
      // when it is the property value that moved under an amount that did not.
      if (amount > 0) return { ...prev, ltv: computeLtv(value, amount).toFixed(2) };
      return source === 'propertyValue' && pct > 0 ? fromPct() : prev;
    });
  };

  /*
   * Which questions this file asks, and the number printed beside each one.
   *
   * Both come from @lenderhound/shared rather than from here, because the notification
   * email has to render the same list under the same numbers. Written out separately in
   * the two files they drifted - different label text on two fields, a DSCR file asking
   * "DSCR" here and reporting "DTI %" there - which is the same two-copies-of-one-rule
   * defect that has now caused three of Henry's complaints.
   *
   * unitsAsked is the one property type whose label no longer implies a count, so it is
   * the one that has to ask; see the note on `units_5_8` in vocabulary.ts.
   */
  const shape = useMemo(() => formShapeOf(d), [d]);
  const L = useMemo(() => formFieldLabels(shape), [shape]);
  const { unitsAsked } = shape;

  /*
   * Cash in hand is asked on a cash-out and on nothing else.
   *
   * Henry, 2026-09-01: "please add 'cash in hand' as a field before occupancy". The
   * transaction type is question 1, so the answer is already on screen by the time the
   * broker gets here - a purchase hands the borrower no proceeds and a rate/term
   * refinance is the transaction defined by taking none, so on two of the three purposes
   * the box has no question to ask. Rendering it greyed on every search would put a
   * permanent dead field in the middle of a twelve-field form to serve a third of them.
   *
   * Same rule as the unit count and reserves below: the form submits what it shows, so a
   * figure typed here and then orphaned by a change of purpose is nulled at submit rather
   * than sent invisibly.
   */
  const { cashOut } = shape;

  // DSCR and DTI are alternatives, not both: a cash-flow program qualifies the
  // property, an income program qualifies the borrower. Showing both invites a broker
  // to fill in the one the grid will ignore.
  const { cashFlow, seasoningRelevant } = shape;
  const funky = d['funkyText'] ?? '';

  /*
   * A cash-flow doc type leaves ONE occupancy on offer.
   *
   * Misha, 2026-09-08: "if someone selects DSCR for Documentation, only 'investment
   * property' should be an option for OCCUPANCY."
   *
   * The pair was already refused downstream - `dscr_on_primary_residence` in
   * contradictions.ts, which is Henry's own request from d6bb5617 after he retracted a NO
   * that turned out to be his typo. That rule catches it AFTER a search is submitted and
   * hands back a question; this stops the combination being expressible, which is what he
   * asked for in the first place ("a good rule to input would be on the parameters, if
   * someone selects DSCR, then primary may not be").
   *
   * IT COSTS NOTHING, MEASURED. Of 4,568 cells published under a DSCR doc type, 4,538 are
   * investment, ZERO are primary residence, and 30 are second home - all 30 at Verus
   * "Foreign National - 2nd Home and Investment" and Onslow Bay "Foreign National Plus".
   *
   * Those 30 are a MIRROR of the same programs' investment rows, not extra reach: 24 and 6
   * cells on each side, the same 75% and 65% maximum LTV, the same FICO floor, the same
   * $1,500,000 and $1,000,000 ceilings. Both lenders print ONE column covering both
   * occupancies - Verus says so in the program name - and extraction expands such a column
   * into a row per value, the same way it expands "PUR/R&T" into two purposes. So a file
   * pushed onto the investment row reads the identical grid under the other name, and
   * neither program is lost.
   *
   * That was checked because the first answer here assumed the 30 cells were reach that
   * this filter would cut off. They are not. If a lender ever publishes a second-home DSCR
   * grid that is genuinely its own, this is the filter to widen - and that would be a
   * measurement, not a scruple.
   */
  const occupancyOptions = useMemo(
    () => (cashFlow ? OCCUPANCIES.filter((o) => o.code === 'investment') : OCCUPANCIES),
    [cashFlow],
  );

  /**
   * Choosing the doc type can move the occupancy under it, so it is not a plain `set`.
   *
   * A broker who picks Primary Residence and then picks DSCR would otherwise leave the
   * occupancy select holding a value it no longer offers. React renders that as the first
   * option while the state stays 'primary' - the box says Investment Property and the
   * search is submitted as a primary residence, which is the one failure mode worse than
   * the error message this replaces. So the value moves with the options.
   *
   * It moves to investment rather than to blank because blank is no longer submittable
   * and there is exactly one legal answer: clearing it would demand a click that has no
   * decision in it. Switching back to an income doc type leaves 'investment' in place -
   * a real answer, visible on screen, and changeable like any other.
   */
  const chooseDocType = (v: string): void => {
    touch();
    setErrors((prev) => (
      prev['docType'] || prev['occupancy'] ? { ...prev, docType: '', occupancy: '' } : prev
    ));
    setD((prev) => (
      formShapeOf({ docType: v }).cashFlow
        ? { ...prev, docType: v, occupancy: 'investment' }
        : { ...prev, docType: v }
    ));
  };

  /** Whether the broker has actually put something in a box, as opposed to left it. */
  const answered = (k: string): boolean => (d[k] ?? '').trim() !== '';

  function submit(e: React.FormEvent): void {
    e.preventDefault();
    // An untouched select submits null, not the empty string.
    //
    // This is the whole of Henry's change at the form boundary: '' is not a member of any
    // vocabulary, so it failed the schema and produced a red message under a field a
    // broker had deliberately left alone. null is a value the schema accepts and the
    // engine widens on.
    const chosen = (k: string): string | null => (d[k] ?? '') || null;
    const candidate = {
      loanPurpose: chosen('loanPurpose'), occupancy: chosen('occupancy'),
      propertyType: chosen('propertyType'),
      // Nulled when the property type does not ask for a count, on the same rule as
      // reserves below: the form submits what it shows. A broker who picks 5+, types 9
      // and then switches to Single Family is not still describing a nine-unit building.
      unitCount: unitsAsked && d['unitCount'] ? Number(d['unitCount']) : null,
      state: chosen('state'), docType: chosen('docType'), citizenship: chosen('citizenship'),
      creditEvent: d['creditEvent'] || 'none',
      // Zero is not a value either of these can hold, so `|| null` cannot swallow a real
      // answer here - unlike on FICO below, where 0 is a real coordinate.
      propertyValue: pv || null, loanAmount: la || null,
      // Nulled when the transaction is not a cash-out, on the same rule as the unit count
      // above: the form submits what it shows. A broker who prices a cash-out at $250,000
      // in hand and then switches the purpose to Purchase is no longer describing a file
      // where anybody takes money away.
      //
      // The guard is on the DRAFT STRING and not on the parsed number, which is what keeps
      // a typed 0 alive: '0' is truthy, 0 is not. Zero is a real answer here - a cash-out
      // that clears a lien and hands the borrower nothing binds no cap anywhere - and it
      // is a different answer from a blank, which binds nothing because we cannot tell.
      cashInHand: cashOut && d['cashInHand']
        ? Number(digits(d['cashInHand'])) : null,
      // Blank is null, and NOT NaN and NOT 0. NaN failed the schema, which was the point
      // when FICO was mandatory; 0 would silently price the file off the no-credit-score
      // grid several lenders publish, which is a different borrower.
      ficoScore: d['ficoScore'] === '' ? null : Number(d['ficoScore']),
      dti: cashFlow || !d['dti'] ? null : Number(d['dti']),
      dscr: !cashFlow || !d['dscr'] ? null : Number(d['dscr']),
      creditEventSeasoningMonths:
        seasoningRelevant && d['creditEventSeasoningMonths']
          ? Number(d['creditEventSeasoningMonths']) : null,
      // No longer nulled on a credit event. It used to be, and correctly, because the
      // seasoning box took its slot: a broker who typed six months and then selected a
      // BK7 was sending a figure they could no longer see. From 2026-09-08 the box does
      // not go anywhere - a credit event ADDS question 13 and reserves becomes 14 - so
      // the form is showing it and the form submits what it shows.
      //
      // Nulled on a cash-out, from 2026-09-14, which is that same rule reached from the
      // other side: the box is off the form on a cash-out, so six months typed on a
      // purchase and then carried into a cash-out is a figure the broker can no longer
      // see and must not be one a reserve floor or a cash-in-hand escape silently reads.
      // The draft keeps it, as it keeps an orphaned cash-in-hand or unit count, so it is
      // back on screen - and sent - if the transaction is switched back.
      //
      // The guard is on the DRAFT STRING for the same reason as cash in hand above: '0'
      // is truthy, 0 is not, and zero months of reserves is a real answer.
      reservesMonths: !cashOut && d['reservesMonths'] ? Number(d['reservesMonths']) : null,
      funkyText: funky,
    };

    /*
     * Every question the form is showing, answered, before anything else is looked at.
     *
     * This runs BEFORE the schema rather than after it because a blank is legal to the
     * schema and always will be: `intakeSchema` types these nullable so that a replay of
     * a stored inquiry, a backfill and the engine's own widening all keep working, and
     * making it strict there would break 146 stored files to enforce a rule about a form.
     * So the requirement lives at the form boundary, which is also the only place that
     * knows WHICH questions are on screen - the same reasoning that already put the
     * conditional seasoning rule here, now applied to the other eleven.
     *
     * Reported all at once, not one at a time. An untouched form comes back with a
     * sentence under every box it needs, which is a page a broker can work down; drip-fed
     * one per click it is eleven round trips to learn the same thing.
     *
     * The two amounts are tested on the DERIVED figures, not on their draft strings. A
     * broker who types an LTV and clicks Search never blurs in a way this render can see,
     * so the loan amount box is still empty while the answer is on screen - see `la`
     * above. Reading the draft here would refuse a form that is correctly filled in.
     */
    const satisfied = (k: string): boolean =>
      k === 'propertyValue' ? pv > 0 : k === 'loanAmount' ? la > 0 : answered(k);
    const unanswered = requiredKeys(shape).filter((k) => !satisfied(k));
    if (unanswered.length > 0) {
      refuse(Object.fromEntries(unanswered.map((k) => [k, FIELD_ERRORS[k]!])));
      return;
    }

    const parsed = intakeSchema.safeParse(candidate);
    if (!parsed.success) {
      // Validate with the same schema the server uses, so the form can never submit
      // something the API will reject - and the broker sees the problem next to the
      // field rather than as a failed search.
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? 'form');
        // `custom` is a .refine, which means a cross-field rule somebody wrote a sentence
        // for. Those sentences are the only ones in the schema written for a reader, and
        // the only ones that describe a relationship the per-field tables cannot see -
        // "Loan amount cannot exceed property value" is about two fields, and this map is
        // keyed on one. Never overwrite them.
        next[key] ??= issue.code === 'custom' ? issue.message
          : answered(key) ? (VALUE_ERRORS[key] ?? issue.message)
            : (FIELD_ERRORS[key] ?? issue.message);
      }
      refuse(next);
      return;
    }

    /*
     * The two amounts again, by the shared function the server and the engine use.
     *
     * Belt and braces on purpose, and cheap: the check above is a form rule and this one
     * is THE rule - missingLtvInputs is the single definition of the condition, the API
     * returns 422 on it and answerScenario throws on it. If the form's own list is ever
     * edited into disagreement with it, this is what stops a file that cannot be answered
     * reaching the engine, and it puts the reason under the field rather than in a red
     * banner after a round trip.
     */
    const missing = missingLtvInputs(parsed.data);
    if (missing.length > 0) {
      refuse(Object.fromEntries(missing.map((k) => [k, FIELD_ERRORS[k]!])));
      return;
    }

    onSubmit(parsed.data);
  }

  return (
    <form onSubmit={submit} className="card" style={{ maxWidth: 940, margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 4px', fontSize: 24 }}>Non QM Scenario Desk</h2>
      {/*
        Henry's welcome, verbatim, spelling and exclamation marks included. It is his
        product's opening line rather than ours, and the two things it does that our copy
        did not are the reason it is at the top: it says what the desk IS to a broker who
        has never seen one, and it asks for the decline reason - which is the single most
        useful sentence a broker can type into the box further down, because a decline
        names the overlay that a rate matrix will never show.

        What it does NOT do is explain what a blank field means, and the paragraph that
        did has moved below the grid rather than gone. See the note there.
      */}
      <p style={{ margin: '0 0 22px', color: 'var(--muted)', fontSize: 14 }}>
        Welcome to the first AI scenario desk for nonqm mortgages. Type in any complicated
        non qm scenario and our system will search through dozens of lenders guidelines
        and let you know which bank can approve your deal. If a loan got recently
        declined, tell us exactly why! The more details the better!
      </p>

      <div className="grid row-3">
        <Field name="loanPurpose" error={errors['loanPurpose']} label={L['loanPurpose']}>
          <Select
            name="loanPurpose" options={LOAN_PURPOSES} placeholder="Select…"
            value={d['loanPurpose'] ?? ''} error={errors['loanPurpose']}
            onChange={set('loanPurpose')}
          />
        </Field>
        <Field
          name="propertyValue" error={errors['propertyValue']} label={L['propertyValue']}
          // No marker. Every field on the form is required except reserves, so the only
          // marker that carries information is the one on reserves - see the note at the
          // top of this file, and the sentence under the grid that states the rule once.
        >
          <input
            id="propertyValue"
            className={`finput${errors['propertyValue'] ? ' field-error' : ''}`}
            inputMode="numeric" placeholder="$ 2,000,000"
            value={d['propertyValue'] ?? ''}
            onChange={(e) => set('propertyValue')(money(e.target.value))}
            onBlur={sync('propertyValue')}
          />
        </Field>
        {/*
          LTV was a coloured read-only readout under the loan amount until Henry asked for
          it the other way round: "he should have the option to enter the ltv which
          automatically populates the loan amount OR can enter the loan amount which
          automatically populates the ltv." So it is a box now, and it keeps the colour -
          red past 80 is the check that catches the stray zero, and it has to survive
          becoming editable or the commonest typo on this form loses its only warning.

          It used to be numbered 3b, on the reasoning that it is not a twelfth question but
          the same question as 3 asked in the units the broker happens to have. Henry saw
          "3, 3b, 5" in a real email and read it as a dropped field, which is fair - a
          reader cannot tell a deliberate gap from a bug. The numbers are now consecutive
          over whatever the form actually asks; see form-fields.ts.

          It sits ABOVE the loan amount rather than below it, which is the same reason it
          became a box at all - Henry, 2026-09-01: "more often than not the broker knows
          the ltv faster than the loan". The order is set once in form-fields.ts, so the
          numbering and the notification email follow this without being edited.
        */}
        <Field name="ltv" error={errors['ltv']} label={L['ltv']}>
          <input
            id="ltv"
            className={`finput${errors['ltv'] ? ' field-error' : ''}`}
            inputMode="decimal" placeholder="75"
            style={{ color: ltvColor, fontWeight: 600 }}
            value={d['ltv'] ?? ''}
            onChange={(e) => set('ltv')(e.target.value.replace(/[^0-9.]/g, ''))}
            onBlur={sync('ltv')}
          />
          <div style={{ marginTop: 6, fontSize: 12, color: 'var(--faint)' }}>
            Fill in either this or the loan amount — the other follows when you leave
            the box. Needs the property value first.
          </div>
        </Field>

        <Field
          name="loanAmount" error={errors['loanAmount']} label={L['loanAmount']}
        >
          <input
            id="loanAmount"
            className={`finput${errors['loanAmount'] ? ' field-error' : ''}`}
            inputMode="numeric" placeholder="$ 1,500,000"
            value={d['loanAmount'] ?? ''}
            onChange={(e) => set('loanAmount')(money(e.target.value))}
            onBlur={sync('loanAmount')}
          />
        </Field>
        {/*
          Cash in hand, question 4, and on screen only for a cash-out.

          It goes here rather than beside the reserves box at the bottom because it is a
          grid dimension, not a nice-to-have: Arc caps it at $500,000 above 65% LTV and
          Logan at $500,000 above 70% (or above 60% on a sub-1.00 DSCR), so on a
          high-leverage cash-out it is the field that decides the answer. It sits directly
          under the LTV box for the same reason - the two are one question at these two
          lenders, and a broker who sees the cap bind can read the fix off the box above
          it: at or under the threshold the cap goes away entirely.

          Hiding it renumbers everything below it: this is question 5 on a cash-out and
          Occupancy is 5 on a purchase. That cost is real and was the argument for the old
          fixed numbering - "question 7" now names a different field depending on the
          purpose, on a form brokers use daily and an AE reads back over the phone.
          Henry decided against it on 2026-09-03 having seen the alternative in his inbox.
          What makes it safe is that the page and the mail are numbered from the same
          list, off the same file, so the two can never disagree - which is the confusion
          he was actually reporting.
        */}
        {cashOut ? (
          <Field name="cashInHand" error={errors['cashInHand']} label={L['cashInHand']}>
            <input
              id="cashInHand"
              className={`finput${errors['cashInHand'] ? ' field-error' : ''}`}
              inputMode="numeric" placeholder="$ 250,000"
              value={d['cashInHand'] ?? ''}
              onChange={(e) => set('cashInHand')(money(e.target.value))}
            />
            {/*
              The caption used to say what a blank does here, because a blank was allowed
              and what it did was bad - there is no set of values to sweep, only a
              published cap with nothing to hold up against it, so the answer came back
              naming the cap and asking rather than clearing the lender. That outcome is
              now unreachable through the form, so the caption says what the number is FOR
              instead, and names the one answer a broker is likeliest to think is a blank.
            */}
            <div style={{ marginTop: 6, fontSize: 12, color: 'var(--faint)' }}>
              What the borrower actually walks away with. Two lenders cap it above 65-70%
              LTV. Enter 0 if they take nothing away - that is an answer, and a good one.
            </div>
          </Field>
        ) : null}

        {/*
          DOCUMENTATION, ASKED BEFORE OCCUPANCY. Misha, 2026-09-08: "move '9. Documentation'
          to be the 5th field, and have 5th field OCCUPANCY appear after it."

          The two are adjacent rather than merely renumbered because the answer here
          narrows the answer below: pick a cash-flow doc type and Investment Property is
          the only occupancy the form will offer. A constraint that arrives after the
          decision it constrains is not a constraint, it is an error message.

          The order comes from form-fields.ts, so the numbers on the page and the numbers
          in the notification mail moved together and cannot disagree.
        */}
        <Field name="docType" error={errors['docType']} label={L['docType']}>
          <Select
            name="docType" options={INTAKE_DOC_TYPES} placeholder="Select…"
            value={d['docType'] ?? ''} error={errors['docType']}
            onChange={chooseDocType}
          />
        </Field>
        <Field name="occupancy" error={errors['occupancy']} label={L['occupancy']}>
          <Select
            name="occupancy" options={occupancyOptions} placeholder="Select…"
            value={d['occupancy'] ?? ''} error={errors['occupancy']}
            onChange={set('occupancy')}
          />
          {cashFlow ? (
            <div style={{ marginTop: 6, fontSize: 12, color: 'var(--faint)' }}>
              A DSCR loan qualifies on the rent the property collects, so it is an
              investment property by definition.
            </div>
          ) : null}
        </Field>
        <Field name="propertyType" error={errors['propertyType']} label={L['propertyType']}>
          <Select
            name="propertyType" options={PROPERTY_TYPES} placeholder="Select…"
            value={d['propertyType'] ?? ''} error={errors['propertyType']}
            onChange={set('propertyType')}
          />
        </Field>
        {unitsAsked ? (
          <Field name="unitCount" error={errors['unitCount']} label={L['unitCount']}>
            <input
              id="unitCount"
              className={`finput${errors['unitCount'] ? ' field-error' : ''}`}
              inputMode="numeric" placeholder="6"
              value={d['unitCount'] ?? ''}
              onChange={(e) => set('unitCount')(digits(e.target.value))}
            />
            {/*
              The option above says 5+ and stops there because the lenders do not agree
              where it ends - 8, 9 and 10 across the six programs that go past four units
              - so the count is what decides which of them may look at the building. It
              used to search as a 5 when left blank, the fewest 5+ can mean and the reading
              that never excludes a lender wrongly. That fallback still exists in the
              evaluator, for stored files that predate this; the form no longer produces
              one.
            */}
            <div style={{ marginTop: 6, fontSize: 12, color: 'var(--faint)' }}>
              Lenders stop at 8, 9 or 10 units, so the count picks which of them can look
              at it.
            </div>
          </Field>
        ) : null}
        <Field name="state" error={errors['state']} label={L['state']}>
          <select
            id="state"
            className={`fselect${errors['state'] ? ' field-error' : ''}`}
            value={d['state'] ?? ''} onChange={(e) => set('state')(e.target.value)}
          >
            <option value="">Select…</option>
            {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>

        <Field name="ficoScore" error={errors['ficoScore']} label={L['ficoScore']}>
          <input
            id="ficoScore"
            className={`finput${errors['ficoScore'] ? ' field-error' : ''}`}
            inputMode="numeric" placeholder="720"
            value={d['ficoScore'] ?? ''}
            onChange={(e) => set('ficoScore')(digits(e.target.value).slice(0, 3))}
          />
          <div style={{ marginTop: 6, fontSize: 12, color: 'var(--faint)' }}>
            Enter 0 for no credit score — several lenders grid it.
          </div>
        </Field>
        <Field
          name={cashFlow ? 'dscr' : 'dti'}
          error={errors[cashFlow ? 'dscr' : 'dti']}
          label={L['qualifier']}
          // Required, like everything else and for its own reason: an income program
          // publishes a maximum DTI and a cash-flow program publishes a minimum DSCR, and
          // a blank cannot be held up against either. Which of the two is asked comes from
          // the documentation answer directly above it.
        >
          <input
            id={cashFlow ? 'dscr' : 'dti'}
            className={`finput${errors[cashFlow ? 'dscr' : 'dti'] ? ' field-error' : ''}`}
            inputMode="decimal"
            placeholder={cashFlow ? '1.15' : '43'}
            value={(cashFlow ? d['dscr'] : d['dti']) ?? ''}
            onChange={(e) => set(cashFlow ? 'dscr' : 'dti')(e.target.value.replace(/[^0-9.]/g, ''))}
          />
        </Field>

        <Field name="citizenship" error={errors['citizenship']} label={L['citizenship']}>
          <Select
            name="citizenship" options={CITIZENSHIPS} placeholder="Select…"
            value={d['citizenship'] ?? ''} error={errors['citizenship']}
            onChange={set('citizenship')}
          />
        </Field>
        <Field name="creditEvent" error={errors['creditEvent']} label={L['creditEvent']}>
          {/* No placeholder: 'none' is a real, valid, default value with its own
              label, so a placeholder would render a second identical "None" that
              submits an empty string instead. */}
          <select
            id="creditEvent" className="fselect"
            value={d['creditEvent'] ?? 'none'}
            onChange={(e) => set('creditEvent')(e.target.value)}
          >
            {CREDIT_EVENTS.map((o) => (
              <option key={o.code} value={o.code}>{o.label}</option>
            ))}
          </select>
        </Field>
        {seasoningRelevant ? (
          <Field
            name="creditEventSeasoningMonths" error={errors['creditEventSeasoningMonths']}
            label={L['creditEventSeasoningMonths']}
            // No marker. It used to carry the form's only "required" chip, because it was
            // the form's only required dropdown-driven field; now it is one of twelve and
            // a chip here would imply the other eleven are not.
          >
            <input
              id="creditEventSeasoningMonths"
              className={`finput${errors['creditEventSeasoningMonths'] ? ' field-error' : ''}`}
              inputMode="numeric" placeholder="48"
              value={d['creditEventSeasoningMonths'] ?? ''}
              onChange={(e) => set('creditEventSeasoningMonths')(digits(e.target.value))}
            />
            <div style={{ marginTop: 6, fontSize: 12, color: 'var(--faint)' }}>
              Months since discharge or completion. The grids band at 24, 36 and 48.
            </div>
          </Field>
        ) : null}
        {/*
          Reserves, on every file but a cash-out. It shared the slot above until
          2026-09-08, and a credit-event file is precisely the file where it matters most:
          the grids that band on seasoning band on reserves beside it.

          Off the form on a cash-out from 2026-09-14, the same way cash in hand is off it
          on everything else. It is the last question, so nothing below it renumbers, and
          the form then carries no "optional" marker at all - which is true, because on a
          cash-out there is no longer an exception to point at.
        */}
        {cashOut ? null : (
          <Field
            name="reservesMonths" error={errors['reservesMonths']}
            label={L['reservesMonths']}
            // The only marker on the form, and the only field it is true of. Henry is the
            // reason it is exempt: "Broker left reserve field blank so for some reason
            // that made it unknown. Lack of info doesn't mean unknown, it should mean
            // approved." A lender printing no reserve rule has nothing to hold a figure
            // up against, so a blank here costs an answer nothing - which is not true of
            // any other box above it.
            note={{ text: 'optional', tone: 'opt' }}
          >
            <input
              id="reservesMonths"
              className={`finput${errors['reservesMonths'] ? ' field-error' : ''}`}
              inputMode="numeric" placeholder="6"
              value={d['reservesMonths'] ?? ''}
              onChange={(e) => set('reservesMonths')(digits(e.target.value))}
            />
          </Field>
        )}
      </div>

      {/*
        The copy that used to open the form. Henry's welcome has the top of the page now,
        and this is not decoration that got displaced to make room for it - it is the one
        place the form states what a blank field DOES, which is the difference between an
        answer and a number a broker can quote. "Optional" read as "does not matter" is a
        widened LTV ceiling taken for a lender's actual maximum.

        Below the grid rather than above it is arguably where it always belonged: it is
        about the eleven boxes directly above, and it is now read after them rather than
        before there is anything to apply it to.

        The exception is named only when the box it names is on screen. On a cash-out there
        is no reserves question, and a sentence carving one out would send the broker
        looking for a field the form has just taken away.

        ONE LINE, since 2026-09-15. It used to carry three more sentences explaining what a
        blank axis does to the answer and that everything is cited. Henry: "Delete this
        besides the first line. Unnecessary paragraph." The rule is the part a broker acts
        on; the reasoning was ours, on his page.
      */}
      <p style={{ margin: '18px 0 0', color: 'var(--muted)', fontSize: 13 }}>
        {cashOut
          ? 'Every question above is required.'
          : 'Every question above is required except reserves.'}
      </p>

      <div style={{ marginTop: 22 }}>
        <label className="lbl" htmlFor="funkyText">
          {FREE_TEXT_QUESTION.label}
          {/*
            This box now decides which half of the product runs. Henry, 2026-08-27: an
            empty box means "search the matrix only", so the label has to say that -
            leaving it blank is a faster, narrower answer rather than simply less colour.
          */}
          <span className="opt">
            optional - blank means the rate matrices only, and a faster answer
          </span>
        </label>
        <textarea
          id="funkyText" className="ftarea" maxLength={FUNKY_MAX}
          // From @lenderhound/shared, with the label above, because the notification
          // mail heads the broker's words with the same two sentences.
          placeholder={FREE_TEXT_QUESTION.prompt}
          value={funky} onChange={(e) => set('funkyText')(e.target.value)}
        />
        {/*
          Examples that stay on screen, rather than a placeholder that disappears the
          moment the broker starts typing.

          Henry, 2026-08-28, on the matrix-only path: "If they don't type the funky stuff
          in the box we have no idea that it's funky." That is the actual exposure - not
          that we skip the guidelines, but that the broker does not recognise their own
          file as unusual. Somebody buying a rental their daughter will live in does not
          think of that as funky; it is just the file. Two lenders decline it outright and
          the grid has no column that would ever show it.

          So these are not decoration. Each one names a situation where the guideline
          overrules a grid that would otherwise say yes, drawn from the scenarios Henry
          supplied - and a placeholder cannot do this job, because it is gone by the time
          the broker is deciding what to write.
        */}
        <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 6, lineHeight: 1.6 }}>
          Worth mentioning: a family member living in the property · borrower lives
          rent-free · recently listed or withdrawn from market · LLC with a low-score
          partner · first-time investor · unusual or unreported income · recent credit
          event · non-warrantable condo
        </div>
        <div style={{ textAlign: 'right', fontSize: 12, color: 'var(--faint)', marginTop: 4 }}>
          {funky.length} / {FUNKY_MAX}
        </div>
      </div>

      <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 14 }}>
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? <><span className="spin" /> &nbsp;Searching…</> : 'Find Matching NonQM Lenders'}
        </button>
        {errors['form'] ? <span className="err-text">{errors['form']}</span> : null}
      </div>
    </form>
  );
}
